import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import moment from 'moment-timezone';
import { Icons } from '../base/BootstrapComponents';
import Loader from '../base/Loader/Loader';
import { CLIENT_MODULE } from '../../configs/moduleNames';
import { getPathNameWithOutParameters } from '../../utils/utils';
import { getActiveBlockName, sortLinksFunction } from '../../utils/layout-utils';
import { AMPP_EDITION, BUILD_VERSION } from '../../configs';
import MiniProfile from '../MiniProfile';
import Notifications from '../../modules/Notifications/components/Notifications';
import GlobalConfirmModal from '../../modules/System/components/GlobalConfirmModal';
import defaultLogo from '../../theme/img/logo.svg';
import amppLogo from '../../theme/img/ampp-logo-cropped.png';
import { getSystemData } from '../../modules/System/reducer';
import UpdatePeriodSelector from './UpdatePeriodSelector';
import TooltipWithPortal from '../base/Tooltip/TooltipWithPortal';
import { checkServicesAvailability } from '../../modules/System/actions';
import { isMenuUnavailable } from '../../modules/System/serviceAvailability';

const getMenuNames = routes => {
  if (!Array.isArray(routes)) {
    return [];
  }

  const names = [];

  const walk = items => {
    items.forEach(route => {
      if (!route || route.hideOnMenu || route.redirect) {
        return;
      }

      if (route.name) {
        names.push(route.name);
      }

      if (Array.isArray(route.children) && route.children.length) {
        walk(route.children);
      }
    });
  };

  walk(routes);

  return [...new Set(names)];
};

export default function Layout({ currentRoutes, currentRoutesByMenu, children }) {
  const currentLogo = AMPP_EDITION ? amppLogo : defaultLogo;
  const dispatch = useDispatch();
  const location = useLocation();
  const intl = useIntl();
  const { formatMessage } = intl;
  const navigate = useNavigate();
  const menuNames = getMenuNames(currentRoutesByMenu);
  const hasServiceMenus = menuNames.length > 0;

  useEffect(() => {
    window.addEventListener('scroll', window.customEventListener.emit, { capture: true });
    window.clientTimeZone = window.localStorage.getItem('USER_TIME_ZONE') || moment.tz.guess();
    return () => {
      window.removeEventListener('scroll', window.customEventListener.emit);
    };
  }, [dispatch]);

  useEffect(() => {
    setMenuOpenState(false);
  }, [navigate]);

  useEffect(() => {
    if (!hasServiceMenus) {
      return undefined;
    }

    dispatch(checkServicesAvailability({ menuNames }));
    const timerId = window.setInterval(() => {
      dispatch(checkServicesAvailability({ menuNames }));
    }, 30000);

    return () => {
      window.clearInterval(timerId);
    };
  }, [dispatch, hasServiceMenus, menuNames.join('|')]);

  const currentPathName = getPathNameWithOutParameters(location.pathname);
  const [activeBlockName, setActiveBlockName] = useState(getActiveBlockName(currentPathName, currentRoutes));
  const [menuOpenState, setMenuOpenState] = useState(false);
  const loginProcess = useSelector(state => (state && state[CLIENT_MODULE] && state[CLIENT_MODULE].loginProcess ? state[CLIENT_MODULE].loginProcess : false));
  const { loadApp, serviceAvailability } = useSelector(getSystemData);

  const handleClickMenuBlock = name => () => {
    let newName = name;
    if (activeBlockName === name) {
      newName = null;
    }
    setActiveBlockName(newName);
  };

  const renderMenuLink = (prop, key, isNested = true) => {
    if (prop.hideOnMenu || prop.redirect) {
      return null;
    }
    let className = prop.className || '';
    const isChildren = Boolean(prop.children && prop.children.length);
    const currentPathName = getPathNameWithOutParameters(location.pathname);
    const newActiveBlockName = getActiveBlockName(currentPathName, currentRoutes);
    if (activeBlockName !== newActiveBlockName) {
      setActiveBlockName(newActiveBlockName);
    }

    const pathName = getPathNameWithOutParameters(prop.path);
    const addProps = {};
    if (isNested) {
      addProps.onClick = handleClickMenuBlock(prop.name);
    }
    if (!isNested) {
      className = classNames(className, 'nested');
    }
    const isOpen = prop.name === activeBlockName;
    const isSelected = (currentPathName.includes(pathName) && pathName !== '/') || (currentPathName === pathName);
    const { MenuComponent = null } = prop;
    const showUnavailableBadge = isNested && isMenuUnavailable(serviceAvailability, prop.name);
    return (
      <div className="bd-toc-item" key={key}>
        <NavLink to={pathName} {...addProps} className={classNames('menu-link', className, { selected: isSelected || isOpen })}>
          {isNested ? (
            <span className="menu-item-icon-wrapper">
              <Icons className="menu-item-icon" icon={prop.icon} customIcon={!!prop.customIcon} />
              {showUnavailableBadge ? <span className="menu-item-status-dot menu-item-status-dot-error" /> : null}
            </span>
          ) : null}
          {MenuComponent ? <MenuComponent prop={prop} isOpen={isOpen} /> : <span className="nav-link-text">{formatMessage({ id: prop.name })}</span>}
          {isChildren && <Icons icon={isOpen ? 'caret-up-fill' : 'caret-down-fill'} className="nested-arrow" width={12} height={12} />}
        </NavLink>
        {isChildren && isOpen
          ? (
            <ul className="nav nested sub-nav-group">
              {prop.children.sort(sortLinksFunction).map((prop, key) => renderMenuLink(prop, key, false))}
            </ul>
          )
          : null}
      </div>
    );
  };

  const sortedRoutes = currentRoutesByMenu.sort(sortLinksFunction);

  return (
    <div className="main-layout">
      <Notifications />
      <Loader loading={loginProcess || loadApp} global hideScreen={loadApp} />
      <div className="app-container">
        <div className={classNames('app-left-column', { active: menuOpenState })}>
          <div className="toggle-button-wrapper" onClick={() => { setMenuOpenState(!menuOpenState); }}>
            {menuOpenState
              ? <Icons customIcon className="toggle-button" icon="arrow-back" />
              : <Icons customIcon className="toggle-button" icon="arrow-forward" />}
          </div>
          <div className="app-menu">
            <nav className="bd-links">
              <ul className="nav">
                {sortedRoutes.map(renderMenuLink)}
                <div className="menu-hr" />
                <div className="bd-toc-item">
                  <NavLink className="menu-link" to="/logout">
                    <Icons customIcon className="menu-item-icon" icon="logout" />
                    <span className="nav-link-text">Выйти</span>
                  </NavLink>
                </div>
              </ul>
            </nav>
            <div className="version-block">
              <div className="version-number">{BUILD_VERSION}</div>
            </div>
          </div>
        </div>
        <div className="app-content-wrapper">
          <div className="app-content">
            <div className="app-bar">
              <div className={classNames('logo-block', { 'ampp-edition': AMPP_EDITION })}>
                <div className="logo">
                  <img src={currentLogo} alt="logo" className={classNames('header-logo', { 'ampp-edition': AMPP_EDITION })} />
                </div>
              </div>
              <TooltipWithPortal
                content={<Icons height={28} width={28} customIcon icon="legend" />}
                tooltipContent={(
                  <div className="legend">
                    <span className="legend-title">Легенда</span>
                    <div className="legend-container">
                      <div className="legend-column">
                        <div className="column-item-container">
                          <span className="column-item-title">Статусы объектов</span>
                          <div className="column-item-value">
                            <div className="column-item-circle " />
                            <div className="column-item-value-description">Доступен</div>
                          </div>
                          <div className="column-item-value">
                            <div className="column-item-circle warning" />
                            <div className="column-item-value-description">Временно недоступен</div>
                          </div>
                          <div className="column-item-value">
                            <div className="column-item-circle unavailable" />
                            <div className="column-item-value-description">Недоступен</div>
                          </div>
                        </div>
                        <div className="column-item-container">
                          <span className="column-item-title">Статусы периферийных модулей</span>
                          <div className="column-item-value">
                            <Icons width={20} height={20} customIcon icon="successful" />
                            <div className="column-item-value-description">В работе</div>
                          </div>
                          <div className="column-item-value">
                            <Icons width={20} height={20} customIcon icon="warning" />
                            <div className="column-item-value-description">Требует внимания</div>
                          </div>
                          <div className="column-item-value">
                            <Icons width={20} height={20} customIcon icon="error" />
                            <div className="column-item-value-description">Неисправен</div>
                          </div>
                        </div>
                        <div className="column-item-container">
                          <span className="column-item-title">Действия</span>
                          <div className="column-item-value">
                            <Icons width={24} height={24} customIcon icon="unlock" />
                            <div className="column-item-value-description">Открыть</div>
                          </div>
                          <div className="column-item-value">
                            <Icons width={24} height={24} customIcon icon="lock-short" />
                            <div className="column-item-value-description">Открыть надолго</div>
                          </div>
                          <div className="column-item-value">
                            <Icons width={24} height={24} customIcon icon="lock" />
                            <div className="column-item-value-description">Закрыть</div>
                          </div>
                          <div className="column-item-value">
                            <Icons width={24} height={24} customIcon icon="play" />
                            <div className="column-item-value-description">Запустить</div>
                          </div>
                          <div className="column-item-value">
                            <Icons width={24} height={24} customIcon icon="stop" />
                            <div className="column-item-value-description">Остановить</div>
                          </div>
                          <div className="column-item-value">
                            <Icons width={24} height={24} customIcon icon="reboot" />
                            <div className="column-item-value-description">Перезагрузить</div>
                          </div>
                          <div className="column-item-value">
                            <Icons width={24} height={24} customIcon icon="power-off" />
                            <div className="column-item-value-description">Включить</div>
                          </div>
                          <div className="column-item-value">
                            <Icons width={24} height={24} customIcon icon="power-on" />
                            <div className="column-item-value-description">Выключить</div>
                          </div>
                        </div>
                      </div>
                      <div className="legend-column with-border">
                        <div className="column-item-container not-color">
                          <span className="column-item-title">Периферийные модули</span>
                          <div className="column-item-value">
                            <Icons width={28} height={28} customIcon icon="ticket_printer" />
                            <div className="column-item-value-description">Принтер талонов </div>
                          </div>
                          <div className="column-item-value">
                            <Icons width={28} height={28} customIcon icon="lpn_camera" />
                            <div className="column-item-value-description">Камера ГРЗ</div>
                          </div>
                          <div className="column-item-value">
                            <Icons width={28} height={28} customIcon icon="nfc_reader" />
                            <div className="column-item-value-description">RFID-считыватель</div>
                          </div>
                          <div className="column-item-value">
                            <Icons width={28} height={28} customIcon icon="intercom" />
                            <div className="column-item-value-description">Интерком</div>
                          </div>
                          <div className="column-item-value">
                            <Icons width={28} height={28} customIcon icon="qr_reader" />
                            <div className="column-item-value-description">Считыватель QR-кода</div>
                          </div>
                          <div className="column-item-value">
                            <Icons width={28} height={28} customIcon icon="barrier" />
                            <div className="column-item-value-description">Шлагбаум</div>
                          </div>
                          <div className="column-item-value">
                            <Icons width={28} height={28} customIcon icon="traffic_light" />
                            <div className="column-item-value-description">Светофор</div>
                          </div>
                          <div className="column-item-value">
                            <Icons width={28} height={28} customIcon icon="info_table" />
                            <div className="column-item-value-description">Табло</div>
                          </div>
                          <div className="column-item-value">
                            <Icons width={28} height={28} customIcon icon="fire_detector" />
                            <div className="column-item-value-description">Пожарный датчик</div>
                          </div>
                          <div className="column-item-value">
                            <Icons width={28} height={28} customIcon icon="charger_station" />
                            <div className="column-item-value-description">Зарядная станция</div>
                          </div>
                          <div className="column-item-value">
                            <Icons width={28} height={28} customIcon icon="cam-range" />
                            <div className="column-item-value-description">Обзорная камера</div>
                          </div>
                          <div className="column-item-value">
                            <Icons width={28} height={28} customIcon icon="barrier_open_single" />
                            <div className="column-item-value-description">Группа въезда</div>
                          </div>
                          <div className="column-item-value">
                            <Icons width={28} height={28} customIcon icon="barrier_close" />
                            <div className="column-item-value-description">Группа выезда</div>
                          </div>
                          <div className="column-item-value">
                            <Icons width={28} height={28} customIcon icon="barrier_open" />
                            <div className="column-item-value-description">Группа переезда</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}
                classNameWrapper="legend-layout-tooltip-wrapper"
                classNameDialog="legend-layout"
              />
              <UpdatePeriodSelector />
              <MiniProfile />
            </div>
            <div className="app-content-children">
              { children }
            </div>
          </div>
        </div>
      </div>
      <div className="frontend-version-badge">
        Фронт: {BUILD_VERSION}
      </div>
      <GlobalConfirmModal />
    </div>
  );
}
